/**
 * How a filter value is compared (`MAT`).
 *
 * @remarks
 * One place, used by every filter field of every picker, so `MAT` is provable
 * on one spot instead of once per picker.
 *
 * The operator names come from KQL rather than from us: an author who wants to
 * know what `has` does looks it up in Microsoft's documentation and finds the
 * right answer. Inventing our own names would cut off that route.
 *
 * @packageDocumentation
 */

import type { ReadField } from '@ns/plugin-picker-shared';
// `zod/v3`, not `zod`: `makeFieldSchema` hands its callback the v3 namespace,
// and a schema built from the v4 one is not assignable to what it expects.
import type { z as zodNamespace } from 'zod/v3';

import { kqlStringLiteral, kqlStringLiteralList } from './kql-literal';

/**
 * The nine operators an author may name, each with the KQL it becomes and the
 * KQL its negation becomes.
 *
 * @remarks
 * `has_any` is the one that does not follow the pattern: it has no `!`-form, so
 * excluding with it goes through `not(...)`. Eight of the nine do have
 * one. Writing that exception down as data rather than as an `if` keeps the
 * builder free of a special case.
 *
 * `has_any_cs` is deliberately absent: an OR over terms is always
 * case-insensitive, and naming it is an author error.
 */
const OPERATORS = {
  '==': { negated: '!=', operand: 'single', positive: '==' },
  '=~': { negated: '!~', operand: 'single', positive: '=~' },
  contains: { negated: '!contains', operand: 'single', positive: 'contains' },
  contains_cs: {
    negated: '!contains_cs',
    operand: 'single',
    positive: 'contains_cs',
  },
  has: { negated: '!has', operand: 'single', positive: 'has' },
  has_any: { negated: undefined, operand: 'list', positive: 'has_any' },
  has_cs: { negated: '!has_cs', operand: 'single', positive: 'has_cs' },
  in: { negated: '!in', operand: 'list', positive: 'in' },
  'in~': { negated: '!in~', operand: 'list', positive: 'in~' },
} as const satisfies Record<
  string,
  {
    readonly negated: string | undefined;
    readonly operand: 'list' | 'single';
    readonly positive: string;
  }
>;

/** Every operator name an author may write. */
export type MatchOperator = keyof typeof OPERATORS;

/**
 * The operator names, for an error message that lists what IS valid.
 *
 * @remarks
 * Listed rather than derived with `Object.keys`, which types its result as
 * `string[]`: TypeScript cannot rule out extra keys at runtime, so narrowing it
 * back needs a cast that asserts what the compiler just refused to promise.
 *
 * The annotation guards one direction: a name here that is not an operator does
 * not compile. The other direction, an operator added to `OPERATORS` and
 * forgotten here, is guarded by a test that compares this list against
 * `Object.keys(OPERATORS)` through the exported type. A test rather than more
 * type machinery, because the failure it has to produce is "you forgot one",
 * and a test says that in one line.
 */
export const MATCH_OPERATOR_NAMES: readonly MatchOperator[] = [
  '==',
  '=~',
  'contains',
  'contains_cs',
  'has',
  'has_any',
  'has_cs',
  'in',
  'in~',
];

/**
 * What an author writes as a filter value.
 *
 * @remarks
 * Three spellings, and the first two are shorthands for the third
 *:
 *
 * ```yaml
 * createdBy: Backstage                      # string -> ==
 * subscriptionType: [generic, sandbox]      # list   -> in
 * pattern: { has_any: [networking, mdp] }   # object -> that operator
 * ```
 *
 * `{ ref: <fieldName> }` is a value expression rather than an operator, and it
 * may appear wherever a literal value may: as the whole value, as an operand,
 * and as an element of a list. That is why
 * {@link resolveMatch} takes operand values as a separate step from the
 * operator: `ref` widens {@link MatchOperand} instead of reworking the
 * operator table.
 */
export type MatchValue =
  | MatchOperand
  | readonly MatchOperand[]
  | Readonly<
      Partial<Record<MatchOperator, MatchOperand | readonly MatchOperand[]>>
    >;

/**
 * A reference to a value the form filler chose.
 *
 * @remarks
 * `path` names the part of an object field to compare. Without one the whole
 * value is compared, and an object without a path is an author error
 *.
 *
 * `optional` says "do not wait for me". Every reference makes the
 * picker wait for its field by default, because a list filtered on a value
 * that is not there yet is a list nobody asked for. Marking it
 * optional takes the field out of that wait, so an empty value simply drops
 * its own comparison and a value that arrives later still filters.
 */
export interface MatchRef {
  readonly optional?: true;
  readonly path?: string;
  readonly ref: string;
}

/** What an author may write where a single value is expected. */
export type MatchOperand = string | MatchRef;

/**
 * The runtime shape of {@link MatchValue}, for a picker's `uiOptions` schema.
 *
 * @remarks
 * Takes the zod implementation as an argument because `makeFieldSchema` hands
 * one to its callback, and a schema built from a SECOND copy of zod is not
 * interchangeable with the one Backstage passes in.
 *
 * What this checks is the SHAPE: a string, a list, or an object whose values
 * are operands. It does not check that an object's key names a real operator;
 * see the comment on the `z.record` below for why, and where that check lives
 * instead.
 *
 * @param z - The zod implementation `makeFieldSchema` supplies.
 * @returns A schema accepting all three spellings a `MatchValue` may take.
 */
export function matchValueSchema(
  z: typeof zodNamespace,
): zodNamespace.ZodType<MatchValue> {
  const matchRef = z.object({
    optional: z.literal(true).optional(),
    path: z.string().optional(),
    ref: z.string(),
  });
  const operand = z.union([z.string(), matchRef]);
  const operandOrList = z.union([operand, z.array(operand)]);
  return z.union([
    operand,
    z.array(operand),
    // The key stays `z.string()` rather than an enum of the operator names.
    // `z.enum` needs a non-empty TUPLE of literals, and neither
    // `Object.keys(OPERATORS)` nor `MATCH_OPERATOR_NAMES` is one: the first is
    // `string[]`, the second is annotated `readonly MatchOperator[]`. Writing
    // the nine names out a third time would be a list that can drift from
    // `OPERATORS`, which is worse than no check here. `resolveMatch` rejects an
    // unknown operator name anyway, and its message names the valid ones, so
    // that is where a template author already gets the answer.
    z.record(z.string(), operandOrList),
  ]);
}

/**
 * The keys that together describe one reference.
 *
 * @remarks
 * Counting every key would make `{ ref: x, path: p }` look like two
 * comparisons, so the ref-beside-a-comparison check counts only the keys that
 * are NOT these.
 */
const REF_KEYS: ReadonlySet<string> = new Set(['ref', 'path', 'optional']);

/**
 * A resolved comparison: which operator, and the values it compares against.
 *
 * @remarks
 * Kept separate from the KQL text so the resolving step (which `ref` will grow
 * into) never has to know about query syntax, and the rendering step never has
 * to know where a value came from.
 */
export interface ResolvedMatch {
  readonly operator: MatchOperator;
  readonly values: readonly string[];
}

/**
 * An author wrote something that cannot be compared.
 *
 * @remarks
 * The message is the whole payload. It names what is wrong and what to write
 * instead, because it ends up in front of the person filling the form, who
 * cannot read this code.
 */
export class MatchAuthorError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'MatchAuthorError';
  }
}

/**
 * Reads an author's filter value into an operator and its operands.
 *
 * @remarks
 * A bare text is equality and case-SENSITIVE. That is a choice: KQL's
 * `==` is case-sensitive and `=~` is not, and the short form follows the
 * stricter of the two. The other way round an author would silently get more
 * than they asked for.
 *
 * @param value - The value as written in `filter` or `exclude`.
 * @param readField - Reads a field the author referenced with `ref`.
 * @returns The operator and the values, or `undefined` when a reference is
 *   empty and the comparison drops.
 * @throws MatchAuthorError - When the value cannot be compared.
 */
export function resolveMatch(
  value: MatchValue,
  readField: ReadField,
): ResolvedMatch | undefined {
  if (typeof value === 'string') {
    return { operator: '==', values: [value] };
  }

  if (isOperandList(value)) {
    if (value.length === 0) {
      throw new MatchAuthorError(
        'An empty list can never match anything. Remove the filter, or name the values it should accept.',
      );
    }
    // The author wrote a non-empty list, so an empty result means every value
    // came from a reference nobody filled in, which drops rather than
    // reports.
    const values = resolveOperands(value, readField);
    return values === undefined ? undefined : { operator: 'in', values };
  }

  const entries = Object.entries(value);
  if (entries.length === 0) {
    throw new MatchAuthorError(
      `An empty object names no comparison. Use one of: ${MATCH_OPERATOR_NAMES.join(
        ', ',
      )}.`,
    );
  }

  const refCase = resolveRefKeys(value, entries, readField);
  if (refCase.handled) return refCase.match;

  if (entries.length > 1) {
    throw new MatchAuthorError(
      `An object names one comparison, but this one names ${entries
        .map(([name]) => name)
        .join(' and ')}. Which of the two is meant cannot be guessed.`,
    );
  }

  // Exactly one entry: the zero and many cases both threw above. An indexed
  // read is still typed as possibly absent, so the fallback is unreachable.
  const [name, operand] = entries[0] ?? ['', undefined];
  if (!isMatchOperator(name)) {
    throw new MatchAuthorError(
      `"${name}" is not a comparison. Use one of: ${MATCH_OPERATOR_NAMES.join(
        ', ',
      )}.`,
    );
  }

  const written = operandsOf(name, operand);
  if (written.length === 0) {
    throw new MatchAuthorError(
      'An empty list can never match anything. Remove the filter, or name the values it should accept.',
    );
  }

  // An operand may itself be a reference, in either shape. Nothing left means
  // every one of them was empty, so the comparison drops.
  const values = resolveOperands(written, readField);
  return values === undefined ? undefined : { operator: name, values };
}

/**
 * Renders a resolved comparison as the right-hand side of a KQL `where`.
 *
 * @remarks
 * When `negate` is set and the operator has no `!`-form, the whole comparison
 * goes into `not(...)`. Measured in the requirements: eight of the
 * nine operators have a `!`-form, `has_any` does not, and `!has_any` comes back
 * as a `BadRequest`.
 *
 * @param column - The column expression, already valid KQL.
 * @param match - The operator and its values.
 * @param negate - Whether this is an `exclude` rather than a `filter`.
 * @returns The comparison, ready to sit behind `| where `.
 */
export function renderMatch(
  column: string,
  match: ResolvedMatch,
  negate = false,
): string {
  const spec = OPERATORS[match.operator];
  const operand =
    spec.operand === 'list'
      ? kqlStringLiteralList(match.values)
      : // A single-operand operator always carries one value: `resolveMatch`
        // rejects an empty list before it can get here.
        kqlStringLiteral(match.values[0] ?? '');

  if (!negate) {
    return `${column} ${spec.positive} ${operand}`;
  }
  if (spec.negated === undefined) {
    return `not(${column} ${spec.positive} ${operand})`;
  }
  return `${column} ${spec.negated} ${operand}`;
}

function isMatchOperator(name: string): name is MatchOperator {
  return Object.hasOwn(OPERATORS, name);
}

function isMatchRef(value: object): value is MatchRef {
  return 'ref' in value;
}

/**
 * `Array.isArray` is typed as `arg is any[]`, which a `readonly` array does not
 * satisfy, so it leaves the list arm of a union in place instead of removing
 * it. This guard states the same test with the type the union actually holds,
 * so the arms after it are the object ones and nothing has to be asserted.
 */
function isOperandList(value: MatchValue): value is readonly MatchOperand[] {
  return Array.isArray(value);
}

/**
 * Handles the two cases in which `ref`, `path` or `optional` decide the answer.
 *
 * @remarks
 * Split out of {@link resolveMatch} so that function stays about comparisons.
 * `handled: false` means this object names no reference at all and the ordinary
 * operator rules apply.
 *
 * @param value - The object the author wrote.
 * @param entries - Its own entries, already read once by the caller.
 * @param readField - Reads a field the author referenced with `ref`.
 */
function resolveRefKeys(
  value: object,
  entries: readonly (readonly [string, unknown])[],
  readField: ReadField,
): { handled: false } | { handled: true; match: ResolvedMatch | undefined } {
  // The keys that could name a comparison. `ref`, `path` and `optional`
  // describe ONE value together, so they never count as a second one.
  const comparisonKeys = entries
    .map(([name]) => name)
    .filter(name => !REF_KEYS.has(name));

  if (isMatchRef(value)) {
    if (comparisonKeys.length > 0) {
      throw new MatchAuthorError(
        `"ref" and "${comparisonKeys[0]}" cannot sit in one object, because "ref" is a value and "${comparisonKeys[0]}" is a comparison. Write { ${comparisonKeys[0]}: { ref: ... } } to compare that field's value, or { ref: ... } on its own to compare it with ==.`,
      );
    }
    return { handled: true, match: resolveRef(value, readField) };
  }

  // A stray ref key only names the reference problem when there is no real
  // comparison beside it. With one, the author wrote two things and the
  // two-comparisons error is the honest answer, exactly as it is without any
  // ref key at all.
  if (comparisonKeys.length === 0) {
    const stray = entries
      .map(([name]) => name)
      .find(name => REF_KEYS.has(name));
    throw new MatchAuthorError(
      `"${stray}" describes a reference, but there is no "ref" naming the field to read. Add one: { ref: <field>, ${stray}: ... }.`,
    );
  }

  return { handled: false };
}

/**
 * The operands this operator was given, as a list, checked against its arity.
 *
 * @remarks
 * Five of the nine operators compare against one value and four against a
 * list, and writing the wrong one is an author error that needs no fetching to
 * spot.
 *
 * @param name - The operator the author named.
 * @param operand - What they wrote behind it.
 */
function operandsOf(name: MatchOperator, operand: unknown): readonly unknown[] {
  const wantsList = OPERATORS[name].operand === 'list';
  const gotList = Array.isArray(operand);

  if (wantsList && !gotList) {
    throw new MatchAuthorError(
      `"${name}" compares against a list of values, so it needs one: { ${name}: [first, second] }.`,
    );
  }
  if (!wantsList && gotList) {
    throw new MatchAuthorError(
      `"${name}" compares against one value, so it takes one: { ${name}: value }.`,
    );
  }
  return gotList ? (operand as readonly unknown[]) : [operand];
}

/**
 * Resolves every reference among these operands, keeping the literals.
 *
 * @remarks
 * A reference may sit anywhere an operand may, so the list branch and the
 * operand branch both pass through here. An empty reference contributes no
 * values rather than an empty term, and a list left with nothing at all drops
 * its whole comparison: every value named a field nobody filled in,
 * so the author did not write an empty list.
 *
 * @returns The values to compare, or `undefined` when nothing is left.
 */
function resolveOperands(
  operands: readonly unknown[],
  readField: ReadField,
): readonly string[] | undefined {
  const values: string[] = [];
  for (const operand of operands) {
    if (
      typeof operand !== 'object' ||
      operand === null ||
      !isMatchRef(operand)
    ) {
      // A literal the author typed, arriving as unchecked YAML. `String()`
      // rather than a cast to `string`: the cast asserts a shape YAML does not
      // guarantee, while a number or a boolean here is a term the author meant
      // literally (`tier: 2`). An object that reached this branch has no `ref`
      // and cannot become a sensible term, so it drops rather than
      // stringifying to "[object Object]".
      if (operand !== null && typeof operand === 'object') continue;
      values.push(String(operand));
      continue;
    }
    const resolved = resolveRef(operand, readField);
    // `undefined` is an empty reference: it drops out and the rest stays.
    if (resolved !== undefined) values.push(...resolved.values);
  }
  return values.length === 0 ? undefined : values;
}

/**
 * Turns one reference into a comparison, or into nothing when the field it
 * names is still empty.
 *
 * @remarks
 * The four shapes a field can have are four different answers. A value
 * compares. Empty drops the comparison rather than matching nothing,
 * because a filler who has not chosen yet asked for everything and not for
 * nothing. The other two are author errors that no filler can fix,
 * so they are reported rather than dropped: a name the form does not have is a
 * typo, and an object without a `path` leaves unsaid which part of
 * it to compare.
 */
function resolveRef(
  ref: MatchRef,
  readField: ReadField,
): ResolvedMatch | undefined {
  const read = readField(ref.ref, ref.path);

  switch (read.kind) {
    case 'value':
      return { operator: '==', values: read.values };
    case 'empty':
      return undefined;
    case 'unknown-field':
      throw new MatchAuthorError(
        `"${
          ref.ref
        }" is not a field of this form. The fields that exist are: ${read.known.join(
          ', ',
        )}.`,
      );
    case 'object':
      throw new MatchAuthorError(
        `"${ref.ref}" holds an object, and which part of it to compare cannot be guessed. Name one with a path: { ref: ${ref.ref}, path: <part> }.`,
      );
    default: {
      const exhaustive: never = read;
      throw new Error(`Unhandled field value: ${JSON.stringify(exhaustive)}`);
    }
  }
}
